import { useState, useEffect } from "react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Settings,
  Plus,
  Search,
  Edit2,
  X,
  Check,
  Filter,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Plane,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../ui/switch";
import {
  getTourServices,
  TourServiceApi,
  createTourService,
  updateTourService,
} from "../../services/tourServiceMasterService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface TourService {
  id: string;
  name: string;
  description: string;
  type: "transport" | "additional";
  isMandatory: boolean;
  isDefaultChecked: boolean;
  isChargeable: boolean;
  status: "active" | "inactive";
  createdDate: string;
}

export const TourServiceMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [editingService, setEditingService] = useState<TourService | null>(
    null,
  );

  const [sortField, setSortField] = useState<"name" | "type" | "status" | null>(
    null,
  );

  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);

  const [filters, setFilters] = useState({
    name: "",
    type: "all",
    mandatory: "all",
    status: "all",
  });

  const [services, setServices] = useState<TourService[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const {
    canAdd,
    canEdit,
    screenName,
    icon: ScreenIcon,
  } = useScreenPermission();

  const totalPages = Math.ceil(totalCount / pageSize);

  const [errors, setErrors] = useState<{
    name?: string;
    description?: string;
    type?: string;
  }>({});

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const mapApiToUI = (api: TourServiceApi): TourService => ({
    id: String(api.tourServiceId),
    name: api.serviceName,
    description: api.description,
    type: api.serviceType,
    isMandatory: api.isMandatory,
    isDefaultChecked: api.isDefaultChecked,
    isChargeable: api.isChargeable ?? false,
    status: api.isActive ? "active" : "inactive",
    createdDate: api.createdAt.split("T")[0],
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
    const loadServices = async () => {
      try {
        setLoading(true);
        const res = await getTourServices({
          searchText: searchTerm || null,
          serviceName: filters.name || null,
          serviceType: filters.type === "all" ? null : filters.type,
          isMandatory:
            filters.mandatory === "all" ? null : filters.mandatory === "yes",
          isActive:
            filters.status === "all" ? null : filters.status === "active",
          pageNo: currentPage,
          pageSize,
        });

        setServices(res.items.map(mapApiToUI));
        setTotalCount(res.totalCount);
      } catch {
        toast.error("Failed to load tour services");
      } finally {
        setLoading(false);
      }
    };

    loadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, currentPage]);

  const reloadServices = async (page = currentPage) => {
    try {
      const res = await getTourServices({
        searchText: searchTerm || null,
        serviceName: filters.name || null,
        serviceType: filters.type === "all" ? null : filters.type,
        isMandatory:
          filters.mandatory === "all" ? null : filters.mandatory === "yes",
        isActive: filters.status === "all" ? null : filters.status === "active",
        pageNo: page,
        pageSize,
      });

      setServices(res.items.map(mapApiToUI));
      setTotalCount(res.totalCount);
    } catch {
      toast.error("Failed to load tour services");
    }
  };

  useEffect(() => {
    reloadServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filters, currentPage, pageSize]);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "transport" as "transport" | "additional",
    isMandatory: false,
    isDefaultChecked: false,
    isChargeable: false,
    status: "active" as "active" | "inactive",
  });

  // const handleSort = (column: string) => {
  //   if (sortColumn === column) {
  //     setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  //   } else {
  //     setSortColumn(column);
  //     setSortDirection("asc");
  //   }
  // };

  const handleSort = (field: "name" | "type" | "status") => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortField(null);
        setSortOrder(null);
      }
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sortedServices = [...services].sort((a, b) => {
    if (!sortField || !sortOrder) return 0;

    const aValue = a[sortField];
    const bValue = b[sortField];

    if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
    if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const displayedServices = sortedServices;

  const handleOpenAddDialog = () => {
    setFormData({
      name: "",
      description: "",
      type: "transport",
      isMandatory: false,
      isDefaultChecked: false,
      isChargeable: false,
      status: "active",
    });
    setIsAddDialogOpen(true);
    setErrors({});
  };

  const handleOpenEditDialog = (service: TourService) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      description: service.description,
      type: service.type,
      isMandatory: service.isMandatory,
      isDefaultChecked: service.isDefaultChecked,
      isChargeable: service.isChargeable,
      status: service.status,
    });
    setIsEditDialogOpen(true);
  };

  const isDuplicateServiceName = (name: string) => {
    return services.some(
      (s) => s.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );
  };

  const nameRegex = /^[A-Za-z\s]+$/;
  const descriptionRegex = /^[A-Za-z\s]+$/;

  const handleAddService = async () => {
    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Service name is required";
    } else if (!nameRegex.test(formData.name.trim())) {
      newErrors.name = "Service name can contain only letters and spaces";
    } else if (formData.name.trim().length > 30) {
      newErrors.name = "Service name cannot exceed 30 characters";
    } else if (isDuplicateServiceName(formData.name)) {
      newErrors.name = "Service name already exists";
    }

    if (
      formData.description &&
      !descriptionRegex.test(formData.description.trim())
    ) {
      newErrors.description = "Description can contain only letters and spaces";
    } else if (formData.description.length > 70) {
      newErrors.description = "Description cannot exceed 70 characters";
    }

    if (!formData.type) {
      newErrors.type = "Service type is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await createTourService({
        serviceName: formData.name,
        description: formData.description,
        serviceType: formData.type,
        isMandatory: formData.isMandatory,
        isDefaultChecked: formData.isDefaultChecked,
        isChargeable: formData.isChargeable,

        isActive: formData.status === "active",
        createdBy: 1,
      });

      toast.success("Service added successfully");

      setIsAddDialogOpen(false);
      setErrors({});
      setCurrentPage(1);
      await reloadServices(1);
    } catch {
      toast.error("Failed to add service");
    }
  };

  const handleUpdateService = async () => {
    if (!editingService) return;

    const newErrors: typeof errors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Service name is required";
    } else if (!nameRegex.test(formData.name.trim())) {
      newErrors.name = "Service name can contain only letters and spaces";
    } else if (formData.name.trim().length > 30) {
      newErrors.name = "Service name cannot exceed 30 characters";
    } else if (
      services.some(
        (s) =>
          s.id !== editingService.id &&
          s.name.trim().toLowerCase() === formData.name.trim().toLowerCase(),
      )
    ) {
      newErrors.name = "Service name already exists";
    }

    if (
      formData.description &&
      !descriptionRegex.test(formData.description.trim())
    ) {
      newErrors.description = "Description can contain only letters and spaces";
    } else if (formData.description.length > 70) {
      newErrors.description = "Description cannot exceed 70 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await updateTourService({
        tourServiceId: Number(editingService.id),
        serviceName: formData.name,
        description: formData.description,
        serviceType: formData.type,
        isMandatory: formData.isMandatory,
        isDefaultChecked: formData.isDefaultChecked,
        isChargeable: formData.isChargeable,
        isActive: formData.status === "active",
        updatedBy: 1,
      });

      toast.success("Service updated successfully");

      setIsEditDialogOpen(false);
      setEditingService(null);

      await reloadServices();
    } catch {
      toast.error("Failed to update service");
    }
  };

  //   const handleDeleteService = (id: string) => {
  //     if (confirm('Are you sure you want to delete this service?')) {
  //       setServices(services.filter(service => service.id !== id));
  //       toast.success('Service deleted successfully');
  //     }
  //   };

  const handleClearFilters = () => {
    setFilters({
      name: "",
      type: "all",
      mandatory: "all",
      status: "all",
    });
    toast.info("Filters cleared");
  };

  // Statistics
  const statistics = {
    total: services.length,
    active: services.filter((s) => s.status === "active").length,
    inactive: services.filter((s) => s.status === "inactive").length,
    transport: services.filter((s) => s.type === "transport").length,
    additional: services.filter((s) => s.type === "additional").length,
    mandatory: services.filter((s) => s.isMandatory).length,
    optional: services.filter((s) => !s.isMandatory).length,
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="sspace-y-4 sm:space-y-6 pb-8 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-4">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search services by name or description..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={async () => {
                    setSearchTerm("");
                    setCurrentPage(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
            {canAdd && (
              <Button
                onClick={handleOpenAddDialog}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Service
              </Button>
            )}
          </div>
        </div>

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-blue-600">Total Services</p>
                    <p className="text-2xl font-bold text-blue-900">
                      {statistics.total}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-600">Active Services</p>
                    <p className="text-2xl font-bold text-green-900">
                      {statistics.active}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-red-600">Mandatory</p>
                    <p className="text-2xl font-bold text-red-900">
                      {statistics.mandatory}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Settings className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-600">Transport</p>
                    <p className="text-2xl font-bold text-amber-900">
                      {statistics.transport}
                    </p>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Plane className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="border-[#5B9BD5]/30">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs text-gray-700 mb-1.5 block">
                    Service Name
                  </Label>
                  <Input
                    placeholder="Filter by name..."
                    value={filters.name}
                    onChange={(e) =>
                      setFilters({ ...filters, name: e.target.value })
                    }
                    className="h-9 text-sm"
                  />
                </div>

                <div>
                  <Label className="text-xs text-gray-700 mb-1.5 block">
                    Type
                  </Label>
                  <Select
                    value={filters.type}
                    onValueChange={(value) =>
                      setFilters({ ...filters, type: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="transport">Transport</SelectItem>
                      <SelectItem value="additional">Additional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-gray-700 mb-1.5 block">
                    Mandatory
                  </Label>
                  <Select
                    value={filters.mandatory}
                    onValueChange={(value) =>
                      setFilters({ ...filters, mandatory: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Mandatory</SelectItem>
                      <SelectItem value="no">Optional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-gray-700 mb-1.5 block">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <Button
                  onClick={handleClearFilters}
                  variant="outline"
                  size="sm"
                  className="text-gray-600 hover:text-gray-900"
                >
                  <X className="w-3.5 h-3.5 mr-1.5" />
                  Clear Filters
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Services Table - Desktop */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ gap: "0px", marginTop: "10px" }}
        >
          <CardContent
            style={{ padding: "0px 0px 20px 0px", paddingBottom: "0px" }}
          >
            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center gap-1">
                        Service Name
                        {sortField === "name" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Description
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("type")}
                    >
                      <div className="flex items-center gap-1">
                        Type
                        {sortField === "type" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Mandatory
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs"
                    >
                      Default
                    </TableHead>
                    <TableHead
                      style={{ color: "#5B9BD5" }}
                      className="px-4 py-3 text-xs cursor-pointer select-none hover:bg-sky-100/50"
                      onClick={() => handleSort("status")}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {sortField === "status" ? (
                          sortOrder === "asc" ? (
                            <ArrowUp className="w-3 h-3" />
                          ) : (
                            <ArrowDown className="w-3 h-3" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3 h-3 opacity-30" />
                        )}
                      </div>
                    </TableHead>
                    {canEdit && (
                      <TableHead
                        style={{ color: "#5B9BD5" }}
                        className="px-4 py-3 text-xs text-right"
                      >
                        Actions
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedServices.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        className="text-center py-8 px-4 text-[#5B9BD5] font-medium"
                      >
                        Loading Tour Services
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedServices.map((service) => (
                      <TableRow key={service.id} className="hover:bg-sky-50/30">
                        <TableCell className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                              <Settings className="w-4 h-4 text-[#5B9BD5]" />
                            </div>
                            <span className="text-gray-900 text-sm">
                              {service.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <span className="text-xs text-gray-700">
                            {service.description}
                          </span>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={
                              service.type === "transport"
                                ? "bg-sky-100 text-sky-700 border-sky-300 text-[10px] px-1.5 py-0.5"
                                : "bg-purple-100 text-purple-700 border-purple-300 text-[10px] px-1.5 py-0.5"
                            }
                          >
                            {service.type === "transport"
                              ? "Transport"
                              : "Additional"}
                          </Badge>
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {service.isMandatory ? (
                            <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] px-1.5 py-0.5">
                              Yes
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-[10px] px-1.5 py-0.5">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          {service.isDefaultChecked ? (
                            <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px] px-1.5 py-0.5">
                              Yes
                            </Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-700 border-gray-300 text-[10px] px-1.5 py-0.5">
                              No
                            </Badge>
                          )}
                        </TableCell>
                        {/* <TableCell className="px-4 py-3">
                          <Badge className={service.isChargeable
                            ? "bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0.5"
                            : "bg-gray-100 text-gray-700 border-gray-300 text-[10px] px-1.5 py-0.5"}
                          >
                            {service.isChargeable ? "Yes" : "No"}
                          </Badge>
                        </TableCell> */}
                        <TableCell className="px-4 py-3">
                          <Badge
                            className={
                              service.status === "active"
                                ? "bg-green-100 text-green-700 border-green-300 text-[10px] px-1.5 py-0.5"
                                : "bg-gray-100 text-gray-700 border-gray-300 text-[10px] px-1.5 py-0.5"
                            }
                          >
                            {service.status}
                          </Badge>
                        </TableCell>
                        {canEdit && (
                          <TableCell className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                onClick={() => handleOpenEditDialog(service)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-[#5B9BD5] hover:bg-sky-100 hover:text-[#5B9BD5]"
                                title="Edit Service"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {/* <Button
                                // onClick={() => handleDeleteService(service.id)}
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-red-500 hover:bg-red-100 hover:text-red-600"
                                title="Delete Service"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button> */}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-100">
              {displayedServices.length === 0 ? (
                <div className="p-8 text-center text-gray-500 text-sm">
                  No services found
                </div>
              ) : (
                displayedServices.map((service) => (
                  <div key={service.id} className="p-4 hover:bg-sky-50/30">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                            <Settings className="w-4 h-4 text-[#5B9BD5]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-gray-900">
                              {service.name}
                            </h3>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {service.description}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-1.5">
                        <Badge
                          className={
                            service.type === "transport"
                              ? "bg-sky-100 text-sky-700 border-sky-300 text-[10px] px-1.5 py-0.5"
                              : "bg-purple-100 text-purple-700 border-purple-300 text-[10px] px-1.5 py-0.5"
                          }
                        >
                          {service.type === "transport"
                            ? "Transport"
                            : "Additional"}
                        </Badge>

                        {service.isMandatory && (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] px-1.5 py-0.5">
                            Mandatory
                          </Badge>
                        )}

                        {service.isChargeable && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300 text-[10px] px-1.5 py-0.5">
                            Chargeable
                          </Badge>
                        )}

                        {service.isDefaultChecked && (
                          <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px] px-1.5 py-0.5">
                            Default
                          </Badge>
                        )}

                        <Badge
                          className={
                            service.status === "active"
                              ? "bg-green-100 text-green-700 border-green-300 text-[10px] px-1.5 py-0.5"
                              : "bg-gray-100 text-gray-700 border-gray-300 text-[10px] px-1.5 py-0.5"
                          }
                        >
                          {service.status}
                        </Badge>
                      </div>

                      <div className="flex gap-2 pt-2 border-t border-gray-100">
                        <Button
                          onClick={() => handleOpenEditDialog(service)}
                          size="sm"
                          variant="outline"
                          className="flex-1 h-8 text-xs border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-sky-50"
                        >
                          <Edit2 className="w-3 h-3 mr-1.5" />
                          Edit
                        </Button>
                        {/* <Button
                          // onClick={() => handleDeleteService(service.id)}
                          size="sm"
                          variant="outline"
                          className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-3 h-3 mr-1.5" />
                          Delete
                        </Button> */}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
          <Pagination
            totalItems={totalCount}
            itemsPerPage={pageSize}
            currentPage={currentPage}
            onPageChange={(page) => setCurrentPage(page)}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
            label="tour services"
          />
        </Card>

        {/* Add Service Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="bg-white sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Add New Service
              </DialogTitle>
              <DialogDescription>
                Create a new tour service configuration
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <InputFieldset
                  label="Service Name"
                  required
                  id="name"
                  value={formData.name}
                  maxLength={30}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, "");

                    setFormData({
                      ...formData,
                      name: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }}
                  placeholder="Enter service name"
                />

                {errors.name && (
                  <p className="text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <InputFieldset
                  label="Description"
                  id="description"
                  value={formData.description}
                  maxLength={70}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, "");

                    setFormData({
                      ...formData,
                      description: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      description: undefined,
                    }));
                  }}
                  placeholder="Enter service description"
                />

                {errors.description && (
                  <p className="text-xs text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Service Type">
                  <Select
                    value={formData.type}
                    onValueChange={(value: "transport" | "additional") => {
                      setFormData({ ...formData, type: value });
                      setErrors((prev) => ({ ...prev, type: undefined }));
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="transport">Transport</SelectItem>
                      <SelectItem value="additional">Additional</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
                {errors.type && (
                  <p className="text-xs text-red-600">{errors.type}</p>
                )}
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="mandatory" className="text-sm">
                    Mandatory Service
                  </Label>
                  <p className="text-xs text-gray-500">
                    Service is required for all tours
                  </p>
                </div>
                <Switch
                  id="mandatory"
                  checked={formData.isMandatory}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isMandatory: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="defaultChecked" className="text-sm">
                    Default Checked
                  </Label>
                  <p className="text-xs text-gray-500">
                    Service is checked by default
                  </p>
                </div>
                <Switch
                  id="defaultChecked"
                  checked={formData.isDefaultChecked}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isDefaultChecked: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="chargeable" className="text-sm">
                    Is Chargeable
                  </Label>
                  <p className="text-xs text-gray-500">
                    Allow this service to be priced in Lawazim
                  </p>
                </div>
                <Switch
                  id="chargeable"
                  checked={formData.isChargeable}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isChargeable: checked })
                  }
                />
              </div>

              <div className="space-y-2">
                <SelectFieldset label="Status" required>
                  <Select
                    value={formData.status}
                    onValueChange={(value: "active" | "inactive") =>
                      setFormData({ ...formData, status: value })
                    }
                  >
                    <SelectTriggerUI>
                      <SelectValue />
                    </SelectTriggerUI>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </SelectFieldset>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddService}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                Add Service
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Service Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle style={{ color: "#5B9BD5" }}>
                Edit Service
              </DialogTitle>
              <DialogDescription>
                Update service configuration
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Service Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  maxLength={30}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, "");

                    setFormData({
                      ...formData,
                      name: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      name: undefined,
                    }));
                  }}
                  placeholder="Enter service name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={formData.description}
                  maxLength={70}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^A-Za-z\s]/g, "");

                    setFormData({
                      ...formData,
                      description: value,
                    });

                    setErrors((prev) => ({
                      ...prev,
                      description: undefined,
                    }));
                  }}
                  placeholder="Enter service description"
                />
                {errors.description && (
                  <p className="text-xs text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-type">Service Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value: "transport" | "additional") =>
                    setFormData({ ...formData, type: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transport">Transport</SelectItem>
                    <SelectItem value="additional">Additional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-mandatory" className="text-sm">
                    Mandatory Service
                  </Label>
                  <p className="text-xs text-gray-500">
                    Service is required for all tours
                  </p>
                </div>
                <Switch
                  id="edit-mandatory"
                  checked={formData.isMandatory}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isMandatory: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-defaultChecked" className="text-sm">
                    Default Checked
                  </Label>
                  <p className="text-xs text-gray-500">
                    Service is checked by default
                  </p>
                </div>
                <Switch
                  id="edit-defaultChecked"
                  checked={formData.isDefaultChecked}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isDefaultChecked: checked })
                  }
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="edit-chargeable" className="text-sm">
                    Is Chargeable
                  </Label>
                  <p className="text-xs text-gray-500">
                    Allow this service to be priced in Lawazim
                  </p>
                </div>
                <Switch
                  id="edit-chargeable"
                  checked={formData.isChargeable}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isChargeable: checked })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: "active" | "inactive") =>
                    setFormData({ ...formData, status: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsEditDialogOpen(false);
                  setEditingService(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateService}
                style={{ background: "#5B9BD5" }}
                className="text-white"
              >
                Update Service
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {totalPages > 1 && (
          <Card className="border-[#5B9BD5]/20">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-sm text-gray-600">
                  Showing {(currentPage - 1) * pageSize + 1} to{" "}
                  {Math.min(currentPage * pageSize, totalCount)} of {totalCount}{" "}
                  venues
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      const page =
                        totalPages <= 5
                          ? i + 1
                          : currentPage <= 3
                            ? i + 1
                            : currentPage >= totalPages - 2
                              ? totalPages - 4 + i
                              : currentPage - 2 + i;

                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          className={
                            currentPage === page
                              ? "h-8 w-8 p-0 bg-[#5B9BD5] text-white"
                              : "h-8 w-8 p-0"
                          }
                        >
                          {page}
                        </Button>
                      );
                    })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
};
